/**
 * Shared integration landing-page template (Story 30.11) — one component
 * parameterized by an Integration data entry. Programmatic SEO: H1 + answer-
 * first intro + key metrics + how-it-works + FAQ, with SoftwareApplication +
 * FAQPage JSON-LD, a breadcrumb, and per-page SEO meta.
 */
import { Link } from "@tanstack/react-router";
import { useSEO } from "../../hooks/useSEO";
import { JsonLd } from "../../seo/JsonLd";
import { faqPageSchema, softwareApplicationSchema } from "../../seo/schemas";
import { Breadcrumb } from "../shared/Breadcrumb";
import { INTEGRATIONS_CTA, type Integration } from "../../data/integrations";

export function IntegrationPage({ integration }: { integration: Integration }) {
  const { slug, name, tagline, intro, keyMetrics, faq } = integration;
  const title = `${name} Reporting for Agencies — ProvenLeap`;

  useSEO({
    title: title.length <= 60 ? title : `${name} Reporting for Agencies`,
    description: `${tagline} ProvenLeap automates ${name} reporting for agencies — all integrations included on every plan.`.slice(0, 158),
    path: `/integrations/${slug}`,
    ogType: "website",
  });

  return (
    <div className="min-h-screen bg-white">
      <JsonLd data={softwareApplicationSchema()} />
      <JsonLd data={faqPageSchema(faq)} />
      <div className="mx-auto max-w-3xl px-4 py-12 sm:py-16">
        <div className="mb-6">
          <Breadcrumb
            items={[
              { name: "Home", url: "/" },
              { name: "Integrations", url: "/integrations" },
              { name, url: `/integrations/${slug}` },
            ]}
          />
        </div>

        <h1 className="text-3xl font-bold tracking-tight text-foreground sm:text-4xl">
          {name} Reporting for Agencies
        </h1>
        <p className="mt-2 text-lg text-foreground/70">{tagline}</p>

        <p className="mt-6 text-[17px] leading-relaxed text-foreground/90">{intro}</p>

        <section className="mt-10" aria-labelledby="metrics-heading">
          <h2 id="metrics-heading" className="text-2xl font-semibold tracking-tight">
            {name} metrics you can report on
          </h2>
          <ul className="mt-4 flex flex-wrap gap-2">
            {keyMetrics.map((m) => (
              <li key={m} className="rounded-full border-[0.5px] border-input px-3 py-1 text-sm text-foreground/80">
                {m}
              </li>
            ))}
          </ul>
        </section>

        <section className="mt-10" aria-labelledby="how-heading">
          <h2 id="how-heading" className="text-2xl font-semibold tracking-tight">How {name} reporting works</h2>
          <ol className="mt-4 space-y-3">
            {[
              { t: "Connect", d: `Add a client and connect ${name} with a secure, auto-refreshing connection.` },
              { t: "Sync", d: `ProvenLeap pulls ${name} data on a schedule — no manual exports.` },
              { t: "Report", d: `Drop ${name} metrics onto a report or dashboard, add your branding, and share a live link or scheduled PDF.` },
            ].map((step, i) => (
              <li key={step.t} className="flex gap-3">
                <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-brand/10 text-sm font-semibold text-brand">
                  {i + 1}
                </span>
                <span className="text-[17px] leading-relaxed text-foreground/90">
                  <strong>{step.t}.</strong> {step.d}
                </span>
              </li>
            ))}
          </ol>
        </section>

        <section className="mt-10" aria-labelledby="faq-heading">
          <h2 id="faq-heading" className="text-2xl font-semibold tracking-tight">Frequently asked questions</h2>
          <dl className="mt-4 space-y-5">
            {faq.map((qa) => (
              <div key={qa.question}>
                <dt className="font-semibold text-foreground">{qa.question}</dt>
                <dd className="mt-1 text-[16px] leading-relaxed text-foreground/85">{qa.answer}</dd>
              </div>
            ))}
          </dl>
        </section>

        <div className="mt-10 rounded-2xl border-[0.5px] border-input bg-muted/40 p-6">
          <p className="text-[16px] text-foreground/90">{INTEGRATIONS_CTA}</p>
          <Link
            to="/waitlist"
            className="mt-3 inline-block text-brand underline underline-offset-2 hover:text-brand/80"
          >
            Join the waitlist →
          </Link>
        </div>
      </div>
    </div>
  );
}
